import React from 'react';
import { useTranslation } from 'react-i18next';
import { AddContactRow } from '@/components/ProfileMenu/NotificationsView/AddContactRow';
import { NotificationSubscriptionBanner } from '@/components/ProfileMenu/NotificationsView/NotificationSubscriptionBanner';
import { SectionFooterLink } from '@/components/ProfileMenu/NotificationsView/SectionFooterLink';
import { SettingRow } from '@/components/ProfileMenu/NotificationsView/SettingRow';
import { useProfileRestriction } from '@/components/ProfileRestriction/useProfileRestriction';
import { LockClosed } from '@/components/Icons/LockClosed';
import { Toggle } from '@/components/Toggle';
import { useShowSubscriptionOffer } from '@/hooks/useShowSubscriptionOffer';
import type { SmsNotificationSettings } from '@/types/notifications';

interface Props {
  settings: SmsNotificationSettings;
  isSubscribed: boolean;
  hasPhone: boolean;
  viewOnly?: boolean;
  onChange: (settings: SmsNotificationSettings) => void;
  onChangePhone: () => void;
  onPurchase: () => void;
}

export const SmsSection: React.FC<Props> = ({
  settings,
  isSubscribed,
  hasPhone,
  viewOnly = false,
  onChange,
  onChangePhone,
  onPurchase,
}) => {
  const { t } = useTranslation();
  const { isRestricted } = useProfileRestriction();
  const showSubscriptionOffer = useShowSubscriptionOffer();

  const isReadOnly = viewOnly || !hasPhone;

  const premiumSubtitle =
    isSubscribed || isRestricted
      ? ''
      : t('notificationSettings.subscriberOnly');

  const premiumToggle = (
    checked: boolean,
    label: string,
    onToggle: (value: boolean) => void,
  ) =>
    isSubscribed ? (
      <Toggle
        checked={checked}
        disabled={isReadOnly}
        label={label}
        onCheckedChange={onToggle}
      />
    ) : (
      <LockClosed className="size-6 shrink-0 text-subtle-foreground" />
    );

  return (
    <>
      {!viewOnly && !hasPhone && (
        <AddContactRow label={t('profileMenu.phone')} onAdd={onChangePhone} />
      )}

      <div className="flex flex-col gap-0.5 px-4">
        {!isRestricted && (
          <p className="text-callout-16 text-foreground">
            {t('notificationSettings.subscriberSectionTitle')}
          </p>
        )}
        <p className="text-body-14 text-subtle-foreground">
          {t('notificationSettings.smsMessageCost')}
        </p>
      </div>

      {showSubscriptionOffer && !viewOnly && (
        <NotificationSubscriptionBanner onPurchase={onPurchase} />
      )}

      <div
        className={`mx-4 flex flex-col rounded-xl bg-background ${viewOnly ? 'mb-4' : ''}`}
      >
        <SettingRow
          title={t('notificationSettings.diaryMessages')}
          subtitle={premiumSubtitle}
          hasDivider
          trailing={premiumToggle(
            settings.isDiaryAnnouncementNotificationsEnabled,
            t('notificationSettings.diaryMessages'),
            (value) =>
              onChange({
                ...settings,
                isDiaryAnnouncementNotificationsEnabled: value,
              }),
          )}
        />
        <SettingRow
          title={t('notificationSettings.dailySummary')}
          subtitle={premiumSubtitle}
          trailing={premiumToggle(
            settings.isEvaluationSummaryEnabled,
            t('notificationSettings.dailySummary'),
            (value) =>
              onChange({ ...settings, isEvaluationSummaryEnabled: value }),
          )}
        />
      </div>

      {!viewOnly && hasPhone && (
        <SectionFooterLink
          label={t('notificationSettings.changePhone')}
          onClick={onChangePhone}
        />
      )}
    </>
  );
};
